export type UTMParameters = {
  utm_campaign?: UtmCampaign // the specific campaign that is driving the traffic. This is often used to track the performance of specific marketing campaigns. utm_campaign=apply_flow_welcome_drip
  utm_content?: UtmContent // Differentiates similar content or links within the same ad or campaign. This is useful for A/B testing and content-targeted ads. utm_content=cta_button
  utm_medium?: UtmMedium //  the medium of the traffic. This could be email, cost-per-click (cpc), social media, or other mediums. utm_medium=email
  utm_source?: UtmSource // the source of the traffic. This could be a search engine (e.g., Google), a newsletter, or another website. utm_source=google
  utm_term?: UtmTerm // the search terms or keywords that brought the traffic. This is mainly used in paid search campaigns. utm_term=running+shoes
}

export type UTMParametersWithFbclid = UTMParameters & {
  fbc?: string
  fbclid?: string
  fbp?: string
}

export type BlockCamelCaseUTM = {
  utmCampaign?: never
  utmContent?: never
  utmMedium?: never
  utmSource?: never
  utmTerm?: never
}

export type SegmentUTMContext = {
  content?: string
  medium?: string
  name?: string
  source?: string
  term?: string
}

export const UTM_MEDIUMS = {
  cpc: 'cpc',
  email: 'email',
  inProduct: 'in-product',
  messenger: 'messenger',
  referral: 'referral', // referral is when a user is referred to a page by another user
  sms: 'sms',
  social: 'social',
} as const

export const UTM_CAMPAIGNS = {
  emrUpsell: 'emr-upsell',
  marketingUpsell: 'marketing-upsell',
  onboardingUpsell: 'onboarding-upsell',
  shareTrial: 'share_trial',
  shareYourStory: 'share_your_story',
} as const

export const UTM_SOURCES = {
  InactivityEmail: 'patient-inactivity-email',
  MissedCallFromSite: 'patient-missed-call-from-site',
  NewUnreadMessageForPatient: 'patient-new-unread-message-for-patient',
  PatientFirstApplyWelcomeDripDay1: 'patient-first-apply-welcome-drip-day-1',
  PatientFirstApplyWelcomeDripDay2: 'patient-first-apply-welcome-drip-day-2',
  PatientFirstApplyWelcomeDripDay3: 'patient-first-apply-welcome-drip-day-3',
  PatientFirstApplyWelcomeDripIntro: 'patient-first-apply-welcome-drip-intro',
  PatientPostApplyDripDay1: 'patient-post-apply-drip-day-1',
  PatientPostApplyDripDay2: 'patient-post-apply-drip-day-2',
  PatientPostApplyDripDay3: 'patient-post-apply-drip-day-3',
  PatientPostApplyDripDay4: 'patient-post-apply-drip-day-4',
  PatientPostApplyDripDay5: 'patient-post-apply-drip-day-5',
  PatientPostApplySVBDay1: 'patient-post-apply-svb-day-1',
  PatientPostApplySVBDay2: 'patient-post-apply-svb-day-2',
  PatientPostApplySVBDay3: 'patient-post-apply-svb-day-3',
  PatientPostApplySVBDay4: 'patient-post-apply-svb-day-4',
  PatientPostApplySVBDay5: 'patient-post-apply-svb-day-5',
  applyFlowReferralScreen: 'apply-flow-referral-screen',
  applyPackageUpdateEmail: 'apply-package-update-email',
  batchedPatientReferralEmail: 'batched-patient-referral-email',
  contactRoundupEmail: 'contact-roundup-email',
  email: 'email',
  facebook: 'facebook',
  followEmailSponsor: 'follow_email_sponsor',
  google: 'google',
  missedCallNotification: 'missed_call_notification',
  notificationNewApply: 'notification_new_apply',
  patientFirstApplyWelcomeDripEmail: 'patient-first-apply-welcome-drip-email',
  patientInboxNewUnreadMessageEmail: 'patient_inbox_new_unread_message_email',
  patientNewUnreadMessage: 'patient_new_unread_message',
  patientRelayEmail: 'patient_relay_email',
  patientRelayInactivityEmail: 'patient_relay_inactivity_email',
  patientScreeningVisitBookingEmail: 'patient_screening_visit_booking_email',
  patientScreeningVisitFollowUpEmail: 'patient_screening_visit_follow_up_email',
  patientScreeningVisitReminderEmail: 'patient_screening_visit_reminder_email',
  patientTrialApplyNextSteps: 'patient-trial-apply-next-steps',
  postTrialApply: 'post_trial_apply',
  recommendationsEmail: 'recommendations_email',
  recordsTab: 'records-tab',
  referral: 'referral',
  registryInvitationEmail: 'registry_invitation_email',
  registryInvitationSms: 'registry_invitation_sms',
  registryRevampPatientInvite: 'registry-revamp-patient-invite',
  registryRoundupEmail: 'registry-roundup-email',
  remarketing: 'remarketing',
  searchLander: 'search_lander',
  siteMonthlyBrief: 'site-monthly-brief',
  siteRelayEmail: 'site_relay_email',
  sms: 'sms',
  sponsorMonthlyBrief: 'sponsor-roundup-email',
  taskAssignedEmail: 'task_assigned_email',
  trialApplicationQualifiedReferral: 'trial-application-qualified-referral',
  trialApplicationReferralOffPlatform:
    'trial-application-referral-off-platform',
  trialApplyLogin: 'trial-apply-login',
  trialFollowUpReminderEmail: 'trial_follow_up_reminder_email',
  trialListing: 'trial_listing',
  trialPerformanceRoundupSite: 'trial_performance_roundup_site',
  trialPerformanceRoundupSiteEmail: 'trial_performance_roundup_site_email',
  trialProNewUnreadMessage: 'trial_pro_new_unread_message',
  trialProNewUnreadMessageEmail: 'trial_pro_new_unread_message_email',
  trialRecommendations: 'trial_recommendations',
} as const

export const UTM_CONTENTS = {
  button: 'button',
  callSiteCta: 'call_site_cta',
  cancelCta: 'cancel_cta',
  confirmCta: 'confirm_cta',
  contactTrialTeamCta: 'contact_trial_team_cta',
  copyLink: 'copy_link',
  emailButton: 'email_button',
  emrConsentCta: 'emr_consent_cta',
  followUpPatientCard: 'follow_up_patient_card',
  headerLogo: 'header_logo',
  inboxUrl: 'inbox_url',
  matchesProfileMessagesUrl: 'matches_profile_messages_url',
  matchesProfileUrl: 'matches_profile_url',
  moreMatchesButton: 'more_matches_button',
  newPatientCard: 'new_patient_card',
  nothingPromisingCta: 'nothing_promising_CTA',
  patientDossierUrl: 'patient_dossier_url',
  patientFirstApplyWelcomeDripDay1Cta: 'PatientFirstApplyWelcomeDripDay1-cta',
  patientFirstApplyWelcomeDripDay2Cta: 'PatientFirstApplyWelcomeDripDay2-cta',
  patientFirstApplyWelcomeDripDay3Cta: 'PatientFirstApplyWelcomeDripDay3-cta',
  patientFirstApplyWelcomeDripIntroCta: 'PatientFirstApplyWelcomeDripIntro-cta',
  patientPartialTrialApplyDrip: 'patientPartialTrialApplyDrip',
  patientPostApplyDripDay2Cta: 'PatientPostApplyDripDay2-cta',
  patientPostApplyDripDay3Cta: 'PatientPostApplyDripDay3-cta',
  patientPostApplyDripDay4Cta: 'PatientPostApplyDripDay4-cta',
  primaryCta: 'primary-cta',
  registryInviteTrialUrl: 'registry_invite_trial_url',
  registryUrl: 'registry_url',
  rescheduleCta: 'reschedule_cta',
  searchLanderShareButton: 'search_lander_share_button',
  securityAlertLink: 'security_alert_link',
  siteUpsellButtonEmr: 'sponsor_upsell_button_emr',
  siteUpsellButtonMarketing: 'sponsor_upsell_button_marketing',
  sponsorMonthlyBriefDistribution: 'distribution_contact_patient_cta',
  sponsorMonthlyBriefPatientCard: 'patient_card_contact_patient_cta',
  sponsorMonthlyBriefRequestData: 'request_data_cta',
  sponsorMonthlyBriefSomebodyElse: 'somebody_else_cta',
  sponsorUpsellButtonEmr: 'sponsor_upsell_button_emr',
  sponsorUpsellButtonMarketing: 'sponsor_upsell_button_marketing',
  sspSignUpUrl: 'ssp_sign_up_url',
  sspTrialRef: 'trial-referral',
  trialClaimUrl: 'trial_claim_url',
  trialPerformanceRoundup: 'trial-performance-roundup',
  unsubscribe: 'unsubscribe_link',
  verifyMySiteLink: 'verify_my_site_link',
  viewAllYourTrialApplies: 'view-all-your-trial-applies',
  viewProfile: 'view-profile',
  viewUncontactedPatientsButton: 'view_uncontacted_patients_button',
  withpowerHomepageLink: 'withpower_homepage_link',
  withpowerTrialApplicationLink: 'withpower_trial_application_link',
} as const

export const UTM_TERMS = {
  patientPartialTrialApplyDrip: 'patientPartialTrialApplyDrip',
  smsWinback: 'sms_winback',
} as const

export type UtmMedium = (typeof UTM_MEDIUMS)[keyof typeof UTM_MEDIUMS]
export type UtmCampaign =
  | (typeof UTM_CAMPAIGNS)[keyof typeof UTM_CAMPAIGNS]
  | DATE_ISO_STRING
export type UtmSource = (typeof UTM_SOURCES)[keyof typeof UTM_SOURCES]
export type UtmContent = (typeof UTM_CONTENTS)[keyof typeof UTM_CONTENTS]
export type UtmTerm = (typeof UTM_TERMS)[keyof typeof UTM_TERMS]

/**
 * Represent a string like `2021-01-08T14:42:34.678Z` (format: ISO 8601).
 */
export type DATE_ISO_STRING =
  `${number}${number}${number}${number}-${number}${number}-${number}${number}T${number}${number}:${number}${number}:${number}${number}.${number}${number}${number}Z`
